/**
 * TaskCreateContext — parties NON-COMPOSANTS : l'objet de contexte et son
 * accesseur `useTaskCreate()`.
 *
 * Extraites de `TaskCreateContext.tsx` pour que ce module-là n'exporte plus QUE
 * son composant provider : Fast Refresh ne fonctionne pas sur un fichier qui
 * mélange composants et exports ordinaires
 * (`react-refresh/only-export-components`). Déplacement PUR — aucun changement
 * de comportement, de signature ni de nom.
 */

import { createContext, useContext } from 'react'

import type { LinkedEntityValue } from '@/lib/linkedEntity'

import type { PendingTaskLink } from './TaskCreateContext'

/** Ce que l'appelant veut savoir en retour. Optionnel : la quasi-totalité des
 *  appelants ouvrent la modale et n'en attendent rien. */
export interface TaskCreateOptions {
    /**
     * Appelé avec l'identifiant de la tâche créée et le lien RETENU dans la modale.
     *
     * 🔴 Sans lui, un appelant qui doit RATTACHER la tâche à l'objet d'où elle
     * part — une échéance d'appel d'offres et sa marque anti-doublon — ne peut
     * rien faire : il ne sait pas laquelle vient de naître. Le lien, parce que la
     * modale laisse le changer ou le retirer : seule une tâche liée à l'AO se
     * rattache à son échéance (PO n° 66).
     */
    onCreated?: (taskId: string, lien: LinkedEntityValue | null) => void
    /**
     * Titre pré-rempli dans la modale — MODIFIABLE ensuite.
     *
     * 🔴 Le titre est obligatoire à la création. Une tâche ouverte depuis un
     * objet qui SAIT de quoi elle parle — une échéance d'appel d'offres, son
     * libellé et la référence du marché — arrivait pourtant vide : on faisait
     * retaper ce que l'écran avait sous les yeux, et la tâche née ne disait plus
     * de quelle échéance elle venait.
     */
    initialTitle?: string | null
    /**
     * Échéance pré-remplie (ISO) — MODIFIABLE ensuite.
     *
     * `TaskCreateDialog` l'accepte depuis toujours ; ce contexte, qui est le
     * seul chemin d'ouverture depuis une ligne de tableau, ne le laissait pas
     * passer. Une tâche née d'une échéance datée arrivait donc sans date.
     */
    initialDueDate?: string | null
}

export interface TaskCreateContextValue {
    /** Ouvre la modale « Nouvelle tâche » liée à cette fiche/ligne. */
    openNewTask: (link: PendingTaskLink, options?: TaskCreateOptions) => void
}

const noop = () => {}

export const TaskCreateContext = createContext<TaskCreateContextValue | null>(null)

/** Accès à l'ouverture de tâche. Sûr hors provider (no-op) — comme le volet. */
export function useTaskCreate(): TaskCreateContextValue {
    return useContext(TaskCreateContext) ?? { openNewTask: noop }
}
